import { loadJsonFile } from "../../forecast-evolution/forecast-evolution-data.js";
import { convertMonthAndDayToPeriodLabel } from "../../forecast-evolution/forecast-evolution-utils.js";
import { restApiDomain, nwccDomain } from "@js/nwcc-domains.js";
/**
 * Retrieves a forecast file containing forecasts for the given stations on
 * the given pub date. The forecast file is retrieved from the REST service.
 *
 * @param {*} year
 * @param {*} pubMonth
 * @param {*} pubDay
 * @param {*} stations - list of network ID's associated with forecast points
 * @param {*} forecastStatus - can be 'final', 'raw', 'prelim'
 * @returns object with the following structure:
 *      {
 *          triplet1: {
 *              'APR-SEP': [5, 1,203,000, 10, 1,103,210, ..., 90, 440,220, 95, 401,441],
 *              'APR-JUL': [...],
 *          },
 *          triplet2: {
 *              ...
 *          }
 *      }
 */
export async function getRESTForecastFile(year, pubMonth, pubDay, stations, forecastStatus = "final") {
    // Convert pub-month and pub-day to zero padded strings
    pubMonth = pubMonth.toString().padStart(2, "0");
    pubDay = pubDay.toString().padStart(2, "0");

    // Create a list of station triplets
    const stationTriplets = stations.map((s) => s + ":*:*").join(",");

    // request the data
    const restService = `${restApiDomain}/forecasts`;
    const dateArgs = `beginPublicationDate=${year}-${pubMonth}-${pubDay}&endPublicationDate=${year}-${pubMonth}-${pubDay}`;
    const url = `${restService}?stationTriplets=${stationTriplets}&forecastStatus=${forecastStatus}&${dateArgs}`;
    const allForecastData = await loadJsonFile(url);

    // intialize the output
    const data = {};
    // process the response
    allForecastData.forEach((forecastPointData) => {
        const triplet = forecastPointData.stationTriplet;
        const tripletData = {};
        forecastPointData.data.forEach((periodData) => {
            const forecastValues = Object.fromEntries(
                Object.entries(periodData.forecastValues).map(([key, value]) => [key, value * 1000]),
            );
            const periodLabel = periodData.forecastPeriod
                .map((p) => {
                    const [m, d] = p.split("-").map(Number);
                    return convertMonthAndDayToPeriodLabel(m, d);
                })
                .join("-");
            const exceedenceData = [];
            Object.entries(forecastValues).forEach(([key, value]) => {
                exceedenceData.push(+key, Number.isNaN(value) ? null : value);
            });
            tripletData[periodLabel] = exceedenceData;
        });
        Object.assign(data, { [triplet]: tripletData });
    });
    return data;
}

/**
 * Retrieves a forecast file containing forecasts for the given stations on
 * the given pub date. The forecast file is retrieved from the JSON Factory.
 *
 * @param {*} element - e.g. SRVO
 * @param {*} freq - e.g. MONTHLY or SEMIMONTHLY
 * @param {*} parameter - e.g. FORECAST, or PERIODMED, or AVG, or DIFF, etc
 * @param {*} dateString - e.g. 2025-4-1
 * @returns  Same format as the getRESTForecastFile method above.
 */
export async function getJSONFactoryForecastFile(element, freq, parameter, dateString) {
    const url = `${nwccDomain}/awdb/data/${element}/${freq}/${parameter}/${element}_${freq}_${parameter}_${dateString}.json`;
    const jsonData = await loadJsonFile(url);
    return jsonData;
}
